import { FOLDER_CUSTOM_INSTRUCTIONS_MAX_LENGTH } from "@frontend-shared/constants";
import type { IChatFolderListObject } from "@frontend-shared/types";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { memo, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { StyledTextarea } from "@/components/StyledTextarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { FORM_VALIDATION_OPTIONS } from "@/lib/validations/form-defaults";

import { FolderKnowledgePanel } from "./FolderKnowledgePanel";

interface IFolderSettingsDialogProps {
  folder: IChatFolderListObject;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSave: (customInstructions: string) => Promise<void>;
}

const FolderSettingsDialogInner: React.FC<IFolderSettingsDialogProps> = ({
  folder,
  isOpen,
  onOpenChange,
  onSave,
}) => {
  const { t } = useTranslation("sidebar");

  const formSchema = useMemo(
    () =>
      z.object({
        customInstructions: z
          .string()
          .max(FOLDER_CUSTOM_INSTRUCTIONS_MAX_LENGTH, {
            message: t("chat_folder_list.folder_settings.max_length", {
              max: FOLDER_CUSTOM_INSTRUCTIONS_MAX_LENGTH,
            }),
          }),
      }),
    [t],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    ...FORM_VALIDATION_OPTIONS,
    resolver: zodResolver(formSchema),
    defaultValues: { customInstructions: folder.customInstructions ?? "" },
  });

  useEffect(() => {
    if (isOpen) {
      form.reset({ customInstructions: folder.customInstructions ?? "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, folder.customInstructions]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSave = async (
    values: z.infer<typeof formSchema>,
  ): Promise<void> => {
    setIsSubmitting(true);
    try {
      await onSave(values.customInstructions.trim());
      onOpenChange(false);
    } catch {
      // NOTE: Dialog stays open so the user can retry.
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {t("chat_folder_list.folder_settings.title")}
          </DialogTitle>
        </DialogHeader>
        {/* NOTE: NSAI-6649 — padding on every side of the scrollport, pulled
            back out with a matching negative margin so the fields keep their
            alignment with the title. A scrolling box clips on both axes, and
            with padding only on the right, the field's border and its focus
            ring were cut off along the left edge. */}
        <div className="-mx-1 space-y-5 max-h-[70vh] overflow-y-auto px-1 py-1">
          <Form {...form}>
            <FormField
              control={form.control}
              name="customInstructions"
              render={({ field, fieldState: { error } }) => (
                <FormItem>
                  <FormLabel>
                    {t("chat_folder_list.folder_settings.instructions_label")}
                  </FormLabel>
                  <FormControl>
                    <StyledTextarea
                      {...field}
                      isError={!!error}
                      rows={5}
                      placeholder={t(
                        "chat_folder_list.folder_settings.instructions_placeholder",
                      )}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </Form>

          <div className="border-t border-border" />

          <FolderKnowledgePanel folderUuid={folder.uuid} />
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            className="grow"
            onClick={() => onOpenChange(false)}
          >
            {t("chat_folder_list.folder_settings.cancel")}
          </Button>
          <Button
            type="button"
            className="grow"
            disabled={isSubmitting}
            onClick={form.handleSubmit(handleSave)}
          >
            {t("chat_folder_list.folder_settings.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const FolderSettingsDialog = memo(FolderSettingsDialogInner);
